import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { getCategories, getMenu } from '../lib/api';
import { Plus, ExternalLink, ShoppingBag } from 'lucide-react';
import { motion } from 'framer-motion';

export default function MenuPage() {
  const { t, lang, getLocalizedField } = useLanguage();
  const { addItem, setIsOpen, itemCount } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeCategory = searchParams.get('category') || 'all';

  useEffect(() => {
    Promise.all([
      getCategories().then(r => setCategories(r.data)),
      getMenu().then(r => setItems(r.data)),
    ]).then(() => setLoading(false)).catch(console.error);
  }, []);

  const groupedItems = useMemo(() => {
    if (activeCategory !== 'all') {
      return { [activeCategory]: items.filter(i => i.category_id === activeCategory) };
    }
    const groups = {};
    categories.forEach(cat => {
      const catItems = items.filter(i => i.category_id === cat.id);
      if (catItems.length > 0) groups[cat.id] = catItems;
    });
    return groups;
  }, [items, categories, activeCategory]);

  const getCat = (catId) => categories.find(c => c.id === catId);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-[hsl(22,10%,38%)]">{t('loading')}</div></div>;

  return (
    <div className="min-h-screen bg-[hsl(38,44%,96%)]">
      {/* Hero */}
      <section className="bg-[hsl(20,17%,12%)] text-white py-16 sm:py-20">
        <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-[family-name:var(--font-display)] text-3xl sm:text-4xl lg:text-5xl tracking-[var(--tracking-display)] mb-3">{t('our_menu')}</h1>
          <p className="text-white/70 max-w-lg mx-auto">103 {t('dishes_count')}</p>
        </div>
      </section>

      {/* Sticky Category Tabs */}
      <div className="sticky top-16 lg:top-[calc(4rem+33px)] z-30 bg-[hsl(38,44%,96%)]/95 backdrop-blur-md border-b border-[hsl(32,17%,84%)]">
        <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto py-3 scrollbar-hide">
            <button onClick={() => setSearchParams({})} className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${activeCategory === 'all' ? 'bg-[hsl(16,55%,36%)] text-white' : 'bg-white border border-[hsl(32,17%,84%)] text-[hsl(22,10%,38%)] hover:bg-[hsl(35,20%,90%)]'}`}>{t('all_categories')}</button>
            {categories.map(cat => (
              <button key={cat.id} onClick={() => setSearchParams({ category: cat.id })} className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${activeCategory === cat.id ? 'bg-[hsl(16,55%,36%)] text-white' : 'bg-white border border-[hsl(32,17%,84%)] text-[hsl(22,10%,38%)] hover:bg-[hsl(35,20%,90%)]'}`}>{getLocalizedField(cat, 'name')}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8 py-8">
        {Object.entries(groupedItems).map(([catId, catItems]) => {
          const cat = getCat(catId);
          return (
            <div key={catId} className="mb-14" id={`cat-${catId}`}>
              {/* Category Header */}
              <div className="relative rounded-2xl overflow-hidden mb-6 h-48 sm:h-56">
                <img src={cat?.image || ''} alt={cat ? getLocalizedField(cat, 'name') : ''} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(20,17%,8%)]/80 via-[hsl(20,17%,8%)]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h2 className="font-[family-name:var(--font-display)] text-2xl sm:text-3xl text-white mb-1">{cat ? getLocalizedField(cat, 'name') : ''}</h2>
                  <p className="text-white/70 text-sm italic">{cat ? (getLocalizedField(cat, 'tagline') || getLocalizedField(cat, 'description')) : ''}</p>
                </div>
              </div>

              {/* Items */}
              <div className="space-y-2">
                {catItems.map((item) => (
                  <div key={item.id} className="bg-white rounded-xl border border-[hsl(32,17%,84%)] overflow-hidden hover:shadow-[var(--shadow-sm)] transition-shadow duration-200">
                    <div className={`flex items-center gap-4 ${item.image ? 'p-0' : 'p-4'}`}>
                      {item.image && (
                        <div className="w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0 overflow-hidden">
                          <img src={item.image} alt={getLocalizedField(item, 'name')} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                      )}
                      <div className={`flex-1 min-w-0 ${item.image ? 'py-3 pr-2' : ''}`}>
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <h3 className="font-[family-name:var(--font-display)] text-base">{getLocalizedField(item, 'name')}</h3>
                          {item.popular && <span className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(42,72%,44%)] bg-[hsl(42,72%,44%)]/10 px-1.5 py-0.5 rounded">Bestseller</span>}
                        </div>
                        {getLocalizedField(item, 'description') && <p className="text-xs text-[hsl(22,10%,38%)] mt-0.5 line-clamp-2">{getLocalizedField(item, 'description')}</p>}
                      </div>
                      <div className={`flex items-center gap-2 flex-shrink-0 ${item.image ? 'pr-4' : ''}`}>
                        <span className="font-bold text-sm text-[hsl(16,55%,36%)] whitespace-nowrap tabular-nums">{item.price.toFixed(2)} €</span>
                        <button onClick={() => addItem({ id: item.id, name: getLocalizedField(item, 'name'), price: item.price, image: item.image })} className="w-9 h-9 rounded-lg bg-[hsl(16,55%,36%)] text-white flex items-center justify-center hover:brightness-[1.05] transition-colors duration-200" data-testid={`add-to-cart-${item.id}`}>
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Lieferando */}
        <div className="mt-8 bg-white rounded-2xl p-6 border-2 border-[hsl(97,18%,34%)] text-center">
          <p className="font-[family-name:var(--font-display)] text-lg mb-3">{t('lieferando_text')}</p>
          <a href="https://www.lieferando.de/speisekarte/little-italy-eberbach" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold bg-[hsl(97,18%,34%)] text-white hover:brightness-[1.1] transition-colors duration-200">{t('lieferando_cta')} <ExternalLink className="w-4 h-4" /></a>
        </div>
      </div>

      {/* Floating cart */}
      {itemCount > 0 && (
        <button onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 z-40 lg:hidden flex items-center gap-2 px-5 py-3 rounded-full bg-[hsl(16,55%,36%)] text-white shadow-lg hover:brightness-[1.05] transition-colors duration-200">
          <ShoppingBag className="w-5 h-5" /><span className="text-sm font-semibold">{itemCount}</span>
        </button>
      )}
    </div>
  );
}
